import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Grid3X3, MessageCircle, User, Search } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultAvatar = 'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=100';

const Messages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchConversations();
    // Poll for new messages
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return defaultAvatar;
    if (photo.startsWith('data:') || photo.startsWith('http')) {
      return photo;
    }
    return `data:image/jpeg;base64,${photo}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const otherUser = conv.other_user;
    const name = otherUser?.display_name || otherUser?.username || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold text-[#FF2E63] mb-3">Messages</h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <Input
              data-testid="search-messages-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="bg-white/5 border-white/10 text-white pl-10 h-10 placeholder:text-[#52525B]"
            />
          </div>
        </div>
      </header>

      {/* Conversations List */}
      <main className="divide-y divide-white/5">
        {loading ? (
          // Skeleton loading
          Array(5).fill(0).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className="w-14 h-14 rounded-full skeleton" />
              <div className="flex-1">
                <div className="h-4 w-24 skeleton rounded mb-2" />
                <div className="h-3 w-40 skeleton rounded" />
              </div>
            </div>
          ))
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <MessageCircle className="w-12 h-12 text-[#52525B] mb-4" />
            <h3 className="text-lg font-semibold text-[#EDEDED] mb-2">
              {searchQuery ? 'No conversations found' : 'No messages yet'}
            </h3>
            <p className="text-sm text-[#A1A1AA] mb-4">
              {searchQuery ? 'Try a different search term' : 'Start a conversation by messaging someone from the grid'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate('/grid')}
                className="bg-[#FF2E63] hover:bg-[#E01E50] text-white"
              >
                Browse People
              </Button>
            )}
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const otherUser = conv.other_user;
            const photo = otherUser?.public_photos?.[0];
            
            return (
              <button
                key={conv.id}
                data-testid={`conversation-${conv.id}`}
                onClick={() => navigate(`/chat/${otherUser?.id}`)}
                className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors text-left"
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <img
                    src={getPhotoUrl(photo)}
                    alt={otherUser?.display_name || otherUser?.username}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  {otherUser?.is_online && (
                    <div className="absolute bottom-0 right-0 w-4 h-4 bg-[#08D9D6] rounded-full border-2 border-[#050505]" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-[#EDEDED] truncate">
                      {otherUser?.display_name || otherUser?.username}
                    </h3>
                    <span className="text-xs text-[#52525B] flex-shrink-0 ml-2">
                      {formatTime(conv.last_message_time)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#A1A1AA] truncate">
                      {conv.last_message || 'Start chatting...'}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="flex-shrink-0 ml-2 w-5 h-5 bg-[#FF2E63] rounded-full flex items-center justify-center">
                        <span className="text-xs text-white font-bold">
                          {conv.unread_count > 9 ? '9+' : conv.unread_count}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default Messages;
